import type { HTMLAttributes, ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn.js';

export type FeedbackVariant = 'error' | 'warning' | 'success' | 'info';

interface FeedbackProps extends HTMLAttributes<HTMLDivElement> {
  variant?: FeedbackVariant;
  /** Drop the leading icon for tight layouts (e.g. inline form errors). */
  icon?: boolean;
  /** Density tweak — sm = thin form-error row, md = standard, lg = full notice. */
  size?: 'sm' | 'md' | 'lg';
  children?: ReactNode;
}

const TONE_VARS: Record<
  FeedbackVariant,
  { bg: string; fg: string; border: string; Icon: LucideIcon }
> = {
  error: {
    bg: 'var(--feedback-error-bg)',
    fg: 'var(--feedback-error-fg)',
    border: 'var(--feedback-error-border)',
    Icon: AlertCircle,
  },
  warning: {
    bg: 'var(--feedback-warning-bg)',
    fg: 'var(--feedback-warning-fg)',
    border: 'var(--feedback-warning-border)',
    Icon: AlertTriangle,
  },
  success: {
    bg: 'var(--feedback-success-bg)',
    fg: 'var(--feedback-success-fg)',
    border: 'var(--feedback-success-border)',
    Icon: CheckCircle2,
  },
  info: {
    bg: 'var(--color-customer-card-bg)',
    fg: 'var(--color-customer-card-accent)',
    border: 'var(--color-border-tertiary)',
    Icon: Info,
  },
};

const SIZE_PAD: Record<NonNullable<FeedbackProps['size']>, string> = {
  sm: 'p-2 text-xs',
  md: 'p-2.5 text-sm',
  lg: 'p-3 text-sm',
};

/**
 * Bordered feedback box — error / warning / success / info.
 *
 * Tracks the active theme through CSS vars (feedback-error-bg etc.) so
 * dark-mode views don't show a too-bright light-mode bg-red-50 panel.
 * Use this anywhere you'd write
 *   <div className="rounded-md border border-red-200 bg-red-50 ... text-red-900">
 */
export function Feedback({
  variant = 'error',
  icon = true,
  size = 'md',
  className,
  style,
  children,
  ...rest
}: FeedbackProps) {
  const tone = TONE_VARS[variant];
  const Icon = tone.Icon;
  return (
    <div
      className={cn(
        'rounded-md border flex items-start gap-2',
        SIZE_PAD[size],
        className,
      )}
      style={{
        backgroundColor: tone.bg,
        color: tone.fg,
        borderColor: tone.border,
        ...style,
      }}
      role={variant === 'error' || variant === 'warning' ? 'alert' : undefined}
      {...rest}
    >
      {icon && <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
